import { autoinject, computedFrom, transient } from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { BindingSignaler } from "aurelia-templating-resources";
import { ValidationController, ValidationRules } from "aurelia-validation";
import * as moment from "moment";
import { CustomValidationRenderer } from "../../../../../../client-validation-system/custom-validation-renderer";
import { DialogServiceConstants } from "../../../../../../constants/basics/dialog.constants";
import { GenericConfirmationDialogModel } from "../../../../../../models/basics/generic-confirmation-dialog-model";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { ChangeInspectionCompanyRequest, RightShipInspectionClientModel, RightShipInspectionType } from "../../../../../../models/rightship-inspections/rightship-inspections.models";
import { EmailRecipientTypeEnum } from "../../../../../../models/vetting/vetting.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { RightShipInspectionsService } from "../../../../../../services/rightship-inspections/rightship-inspections.service";
import { ValidationsClientService } from "../../../../../../services/validations/validations-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { EmailValidator } from "../../../../../../utilities/email-validator";

@autoinject
@transient()
export class ChangeRightshipInspectionCompanyDialog 
{
    
    inspection: RightShipInspectionClientModel;
    onSave: () => void;
    _resettingMaxDate: boolean = false;
    emailMessage: string = "";
    notifyRelevantParties: boolean = true;
    validationResult: { [property: string]: boolean } = {};

    emails: {
        emailRecipientTypeId?: number,
        email: string,
        isInspectionCompanyEmail?: boolean
    }[] = [];

    @computedFrom("configService.config.settings.rightshipInspectionEmail")
    get rightshipEmail()
    {
        return this.configService.config.settings.rightshipInspectionEmail;
    }

    constructor(private dialogClientService: DialogClientService, private bindingSignaler: BindingSignaler, private rightshipInspectionService: RightShipInspectionsService,
        private i18N: I18N, private notificationService: NotificationClientService,
        private validationsClientService: ValidationsClientService,
        private validationController: ValidationController, private configService: ConfigService) {

        this.validationController.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: { item: RightShipInspectionClientModel, onSave: () => void }) {
        if (!model) return;
        this.inspection = CloneHelper.cloneObject(model.item);
        this.onSave = model.onSave;
        this.setEmailMessage();
        this.addValidationRules();
    }

    attached()
    {
        this.addInspectionEmails();
    }

    addValidationRules() {
        ValidationRules
            .ensure<RightShipInspectionClientModel, number>(x => x.inspectingCompanyId)
            .displayName('Inspection Company')
            .required()

            .ensure<number>(x => x.countryId)
            .displayName('Country')
            .required()

            .ensure<number>(x => x.portId)
            .displayName('Port')
            .required()
            
            .ensure<Date>(x => x.actualDateOfInspection)
            .displayName('Inspection Date')
            .required()
            .when(() => this.isCargill)

            .ensure<Date>(x => x.requestedDateOfInspection)
            .displayName('Inspection Date')
            .required()
            .when(() => !this.isCargill)

            .ensure<boolean>(x => x.isHybrid)
            .displayName('Inspection Type')
            .required()

            .ensure<number>(x => x.lengthOfTimeAtBerth)
            .displayName('Length Of Time At Berth')
            .required()
            .on(this.inspection);

            //closeout email validations
            ValidationRules
            .ensure<ChangeRightshipInspectionCompanyDialog, any[]>(x => x.emails)
            .required()
            .withMessage("At least one email is required")
            .when(x => x.notifyRelevantParties == true)
            .satisfies(emails => emails.filter(e => e.email != null && e.email.trim() != "" && EmailValidator.isValidEmail(e.email) && e.emailRecipientTypeId == EmailRecipientTypeEnum.To).length > 0)
            .withMessage("At least 1 TO email is required")
            .when(x => x.notifyRelevantParties == true)
            .on(this);
    }

    @computedFrom('inspection', 'inspection.inspectionTypeId')
    get isWet() {
        return this.inspection && this.inspection.inspectionTypeId == RightShipInspectionType.TankerInspection;
    }

    @computedFrom('inspection', 'inspection.inspectionTypeId')
    get isCargill() {
        return this.inspection && this.inspection.inspectionTypeId == RightShipInspectionType.CargillSurvey;
    }

    @computedFrom('inspection', 'inspection.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthDays() {
        return this.inspection ? Math.floor(this.inspection.lengthOfTimeAtBerth) : null;
    }

    set lengthOfTimeAtBerthDays(x: number) {
        this.inspection.lengthOfTimeAtBerth = x + Number((this.lengthOfTimeAtBerthHours / 24).toFixed(5));
    }

    @computedFrom('inspection', 'inspection.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthHours() {
        const val = this.inspection ? Math.round((this.inspection.lengthOfTimeAtBerth - this.lengthOfTimeAtBerthDays) * 24) : null;
        return val;
    }

    @computedFrom('inspection', 'inspection.lengthOfTimeAtBerth')
    get lengthOfTimeAtBerthTotalHours() {
        const val = this.inspection && this.inspection.lengthOfTimeAtBerth ? this.inspection.lengthOfTimeAtBerth * 24: 0;
        return Math.ceil(val);
    }

    setIsHybrid(isHybrid: boolean) {
        this.inspection.isHybrid = isHybrid;
        this.setEmailMessage();
    }

    @computedFrom('inspection', 'inspection.estimatedArrivalDate', 'inspection.lengthOfTimeAtBerth', '_resettingMaxDate')
    get maxRequestDate() {
        if (this._resettingMaxDate) {
            setTimeout(() => {
                this._resettingMaxDate = false;
            }, 150);
            return null;
        }

        if (this.inspection && this.inspection.estimatedArrivalDate && this.inspection.lengthOfTimeAtBerth) {
            const maxDate = moment(this.inspection.estimatedArrivalDate).add(this.lengthOfTimeAtBerthDays + (this.lengthOfTimeAtBerthHours > 0 ? 1 : 0) - 1, "days");

            return maxDate.toDate().setHours(23, 59, 59);
        }
        else return null;
    }

    set lengthOfTimeAtBerthHours(x: number) {
        const val = this.lengthOfTimeAtBerthDays + Number((x / 24).toFixed(5));
        this.inspection.lengthOfTimeAtBerth = val;
        setTimeout(() => {
            this.bindingSignaler.signal('update');
        }, 200);
    }

    countryChanged(event: { item: LookupValue, oldItem: LookupValue }) {
        if (event && event.item && Object.keys(event.item.additionalData).indexOf('isSanctioned') > -1 && event.item.additionalData['isSanctioned'] == true) {
            const model = new GenericConfirmationDialogModel();
            model.confirm = "Ok";
            model.messages.push('Country is sanctioned and cannot be selected');
            model.title = 'Sanctioned Country';
            model.onConfirm = () => {
                this.inspection.portId = null;
                this.inspection.countryId = null;
            };
            this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
        }
    }

    close() {
        this.dialogClientService.hide();
    }

    removeEmail(index) {
        this.emails.splice(index, 1);
    }

    addEmail(email: string = null, emailRecipientTypeId: number = null, addfirst: boolean = false, isInspectionCompanyEmail: boolean = false, recipientType: string = '') {
        if (recipientType != '(Technical Manager)' && recipientType != '(Agent)'){
            const emailModel = {
                email: email != null && email.trim() != ''? email: '',
                emailRecipientTypeId: emailRecipientTypeId != null && emailRecipientTypeId > 0? emailRecipientTypeId: null,
                isInspectionCompanyEmail: isInspectionCompanyEmail,
                recipientType: recipientType
            };
            addfirst? this.emails.unshift(emailModel): this.emails.push(emailModel);
        }
    }

    async save()
    {
        try {
            let validationResponse = await this.validationController.validate({object: this.inspection});

            if (!validationResponse.valid) return;

            validationResponse = await this.validationController.validate({object: this});

            if (!validationResponse.valid) 
            {
                this.validationResult = Object.assign({}, ...validationResponse.results.map(x => {return { [x.propertyName]: !x.valid }; }));
                const validationMessage = validationResponse.results.filter(x => x.valid == false && x.message).join("\n");
                this.notificationService.show(this.i18N.tr('error-text'), validationMessage, NotificationType.Error);
                return;
            }

            const response = await this.rightshipInspectionService.changeInspectionCompany(<ChangeInspectionCompanyRequest>{
                rightShipInspectionId: this.inspection.rightShipInspectionId,
                inspectionDate: this.isCargill? this.inspection.actualDateOfInspection: this.inspection.requestedDateOfInspection,
                inspectionCompanyId: this.inspection.inspectingCompanyId,
                countryId: this.inspection.countryId,
                portId: this.inspection.portId,
                lengthOfTimeAtBerth: this.inspection.lengthOfTimeAtBerth,
                isHybrid: this.inspection.isHybrid,
                notifyRelevantParties: this.notifyRelevantParties,
                emails: this.emails,
                emailMessage: this.emailMessage
            });

            if (response.validationResult) {
                this.validationsClientService.showValidationErrors(response.validationResult);
                return;
            }

            if(this.onSave) this.onSave();
            
            this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);

            this.close();
        }
        catch (e)
        {
            console.error(e);
            this.notificationService.show(this.i18N.tr('error-text'), "Failed to change inspection company", NotificationType.Error);
        }
    }

    inspectionCompany: string = null;
    
    inspectingCompanyChanged(item, oldItem) {
        this.debounce(() => {
            const companyChanged = item && (!oldItem || (oldItem && item.id != oldItem.id));
            if (!companyChanged) return;

            if(this.inspection && this.inspection.inspectingCompanyId && oldItem && oldItem.additionalData) {   
                
                const emailsToRemove = this.emails.filter(x => x.isInspectionCompanyEmail);
                
                emailsToRemove.forEach(emailToRemove => {
                    const index = this.emails.indexOf(emailToRemove);

                    if (index > -1) {
                        this.emails.splice(index, 1);
                    }
                });                
            }

            if (item && item.additionalData) {
                this.splitEmailsAndAdd(item.additionalData.toEmail, EmailRecipientTypeEnum.To, true, true, '(Inspecting Company)');
                if(item.additionalData.ccEmail)
                    this.splitEmailsAndAdd(item.additionalData.ccEmail, EmailRecipientTypeEnum.Cc, false, true, '(Inspecting Company)');
                if(item.additionalData.bccEmail)
                    this.splitEmailsAndAdd(item.additionalData.bccEmail, EmailRecipientTypeEnum.Bcc, false, true, '(Inspecting Company)');
                this.inspectionCompany = item.name;
                this.setEmailMessage();
            }
        }, 100);
    }

    private splitEmailsAndAdd(emailString: string, emailRecipientType: EmailRecipientTypeEnum, addFirst: boolean, isInspectionCompanyEmail: boolean, recipientType: string) {
        
        if (!emailString || emailString.trim().length == 0) {
            this.addEmail('', emailRecipientType, addFirst, isInspectionCompanyEmail, recipientType);
            return;
        }
        
        const emails = emailString.split(',');

        emails.forEach(email => {

            email = email.trim();

            if (email) {
                this.addEmail(email, emailRecipientType, addFirst, isInspectionCompanyEmail, recipientType);
            }
        });
    }

    private addInspectionEmails() {
        if (this.inspection && this.inspection.emails && this.inspection.emails.filter(x => x.isTechnicalManagerEmail == true && x.email)) {
            this.inspection.emails.filter(x => x.isTechnicalManagerEmail == true && x.email).forEach(email => {                
                this.splitEmailsAndAdd(email.email, EmailRecipientTypeEnum.Cc, false, false, email.recipientType);
            });
        }

        this.splitEmailsAndAdd(this.rightshipEmail, EmailRecipientTypeEnum.Cc, false, false, '(Dry Inspections)');

        if(this.emails.length <= 0){
            this.addEmail();
        }
    }

    private debounceTimeout;
    debounce = (func: (...args) => any, wait: number = 100, immediate: boolean = false, ...args)  =>{

        const later = function (...args) {
            this.debounceTimeout = null;
            if (!immediate) func.apply(this, args);
        };

        clearTimeout(this.debounceTimeout);

        this.debounceTimeout = setTimeout(later, wait);

        if (immediate) func.apply(this, args);
    }

    setEmailMessage() {
        const identificationNumberType = this.inspection.vesselImo ? 'IMO': 
                this.inspection.europeanNumberOfIdentification ? 'ENI': 
                this.inspection.vehicleIdentificationNumber ? 'VIN' : '';
        const identificationNumber = this.inspection.vesselImo ? this.inspection.vesselImo : 
                this.inspection.europeanNumberOfIdentification ? this.inspection.europeanNumberOfIdentification : 
                this.inspection.vehicleIdentificationNumber ? this.inspection.vehicleIdentificationNumber:'';

        this.emailMessage = `Good day,<br/><br/>`;
            
        this.emailMessage += `RightShip is requesting for an inspector to perform a ${this.inspection.isHybrid? 'hybrid': 'standard'} inspection for <b>${this.inspection.vesselName} (${identificationNumber})</b>.<br/><br/>`;
            
        this.emailMessage += 'Please find below the details:';
        this.emailMessage += '<div style="margin: 24px 0;"><b style="font-size: 18px;">Vessel details</b>' +
            '<div style="width: 100%; height: 1px; margin: 8px 0 24px; background-color: #D0D0D0;"></div>' +
            '<table border="0" cellpadding="0" cellspacing="0" width="100%">' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">${identificationNumberType}</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${identificationNumber}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Vessel</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.vesselName}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Date of build</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${moment(this.inspection.vesselDateOfBuild).format("YYYY")}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Vessel type</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.vesselType}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">DWT</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.vesselDwt}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Flag</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.vesselFlag}</p></td>` +
                '</tr>' +
            '</table>' +
            '</div>';

        this.emailMessage += '<div style="margin: 24px 0;"><b style="font-size: 18px;">Port and operational details</b>' +
            '<div style="width: 100%; height: 1px; margin: 8px 0 24px; background-color: #D0D0D0;"></div>' +
            '<table border="0" cellpadding="0" cellspacing="0" width="100%">' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Port</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.port}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Country</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.country}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Est. arrival date</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${moment(this.inspection.estimatedArrivalDate).format('MMMM Do YYYY')}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Length of time at berth</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.lengthOfTimeAtBerthDays}${this.lengthOfTimeAtBerthDays == 1? ' day':' days'}${this.lengthOfTimeAtBerthHours > 0? ' and ':''}${this.lengthOfTimeAtBerthHours > 0? this.lengthOfTimeAtBerthHours:''}${this.lengthOfTimeAtBerthHours > 0? this.lengthOfTimeAtBerthHours == 1? ' hour':' hours':''}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Cargo operation</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.cargoOperation}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Requested date of inspection</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${moment(this.inspection.requestedDateOfInspection).format('MMMM Do YYYY')}</p></td>` +
                '</tr>' +
            '</table>' +
            '</div>';

        this.emailMessage += '<div style="margin: 24px 0;"><b style="font-size: 18px;">Agent details</b>' +
            '<div style="width: 100%; height: 1px; margin: 8px 0 24px; background-color: #D0D0D0;"></div>' +
            '<table border="0" cellpadding="0" cellspacing="0" width="100%">' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Agent name</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.agentName}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Agent office number</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.agentOfficeNumber}</p></td>` +
                '</tr>' +
                '<tr>' +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Agent email address</p></td>` +
                    `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.agentEmail}</p></td>` +
                '</tr>';

        if (this.inspection.inspectionRequestComments){
            this.emailMessage += '<tr>' +
            `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-weight: bold; font-size: 15px;">Inspection Request Comment</p></td>` +
            `<td style="width: 50%;"><p style="margin-bottom: 8px; font-family: 'Roboto', Helvetica, sans-serif; font-size: 15px;">${this.inspection.inspectionRequestComments}</p></td>` +
        '</tr>';
        }

        this.emailMessage += '</table>'+
        '</div>';

        this.emailMessage += '<div><table><tr><td></td></tr></table></div>'; //Do not delete this div - DEL-17528
        this.emailMessage += '{{PSC and INC Snapshot}}';
    }

    @computedFrom('inspection', 'inspection.inspectionTypeId')
    public get inspectionType() {
        if (this.inspection) {
            switch (this.inspection.inspectionTypeId) {
                case 1: return "RightShip";
                case 2: return "Cargill";
                case 3: return "Tanker";
            }
        }

        return "";
    }

    clearValidation(property: string, index: number)
    {
        if(this.validationResult && Object.keys(this.validationResult).indexOf(property) > -1 && index == 0)
            this.validationResult[property] = false;
    }
}